// src/pages/TransferProfiles.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { PhoneForwarded, Plus, Edit2, Trash2, X, Search } from 'lucide-react';
import { transferProfilesService, TransferProfile } from 'api/services/transferProfiles.service';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import OrganizationFilter from 'components/Filters/OrganizationFilter';

const MODES = ['SIPRefer', 'SIPDial', 'liveAgent'] as const;
const SCOPES = ['destination', 'destination_series', 'default'] as const;

const emptyForm = (): TransferProfile => ({
  scope_type: 'destination',
  scope_value: '',
  mode: null,
  refer_to_template: '',
  dial_dest_template: '',
  headers: {},
  enabled: true,
  name: '',
  notes: ''
});

const TransferProfiles: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();
  const isPlatformAdmin = currentRole === 'master_admin' || currentRole === 'super_admin';

  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [profiles, setProfiles] = useState<TransferProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<TransferProfile | null>(null);
  const [form, setForm] = useState<TransferProfile>(emptyForm());
  const [headerRows, setHeaderRows] = useState<{ key: string; value: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [caps, setCaps] = useState<any>(null);

  // For admins we scope by the selected org; non-admins use their own org.
  const orgId = isPlatformAdmin ? selectedOrgId : (currentOrganization?.id || '');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await transferProfilesService.list(orgId || undefined);
    if (res.success) {
      setProfiles(res.data || []);
    } else {
      setError(res.error || 'Failed to load transfer profiles');
      setProfiles([]);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  // Resolve which provider/capabilities this org's transfers run on (for mode hints + guard).
  useEffect(() => {
    (async () => {
      const r = await transferProfilesService.capabilities(orgId || undefined);
      setCaps(r.success ? r.data : null);
    })();
  }, [orgId]);

  // Whether a mode is executable by the resolved provider (null caps/unresolved = allow).
  const modeSupported = (mode?: string | null): boolean => {
    if (!mode || !caps || !caps.resolved) return true;
    if (mode === 'SIPRefer') return !!caps.supportsRefer;
    if (mode === 'SIPDial') return !!caps.supportsDial;
    if (mode === 'liveAgent') return !!caps.supportsDial || !!caps.supportsConference;
    return true;
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setHeaderRows([]);
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (p: TransferProfile) => {
    setEditing(p);
    setForm({ ...p });
    setHeaderRows(Object.entries(p.headers || {}).map(([key, value]) => ({ key, value: String(value) })));
    setFormError(null);
    setShowModal(true);
  };

  const handleDelete = async (p: TransferProfile) => {
    if (!p.id || !window.confirm(`Delete transfer profile "${p.name || p.scope_value}"?`)) return;
    setDeleting(p.id);
    const res = await transferProfilesService.remove(p.id);
    setDeleting(null);
    if (res.success) load();
    else alert(res.error || 'Delete failed');
  };

  const handleSave = async () => {
    setFormError(null);

    if (!form.scope_value?.trim() && form.scope_type !== 'default') {
      setFormError('Destination (scope value) is required');
      return;
    }
    // Mode-aware template requirements
    if (form.mode === 'SIPDial' && !form.dial_dest_template?.trim()) {
      setFormError('SIPDial mode requires a Dial destination template');
      return;
    }
    if (form.mode === 'SIPRefer' && !form.refer_to_template?.trim()) {
      setFormError('SIPRefer mode requires a Refer-To template');
      return;
    }
    // Capability guard: don't let a profile use a mode the resolved provider can't execute.
    if (form.mode && !modeSupported(form.mode)) {
      setFormError(`The resolved provider${caps?.providerName ? ` (${caps.providerName})` : ''} can't execute ${form.mode}. Choose a supported mode or reassign the provider.`);
      return;
    }
    // Build headers from the key/value rows (skip blank keys, reject dup keys)
    const headers: Record<string, string> = {};
    for (const r of headerRows) {
      const k = r.key.trim();
      if (!k) continue;
      if (headers[k] !== undefined) {
        setFormError(`Duplicate header name: ${k}`);
        return;
      }
      headers[k] = r.value;
    }

    const payload: TransferProfile = {
      ...form,
      scope_value: form.scope_type === 'default' ? '*' : form.scope_value.trim(),
      mode: form.mode || null,
      headers
    };

    setSaving(true);
    let res;
    if (editing?.id) {
      res = await transferProfilesService.update(editing.id, payload);
    } else {
      if (!orgId) {
        setSaving(false);
        setFormError('Select an organization first');
        return;
      }
      res = await transferProfilesService.create(payload, orgId);
    }
    setSaving(false);

    if (res.success) {
      setShowModal(false);
      load();
    } else {
      setFormError(res.error || 'Save failed');
    }
  };

  const filtered = profiles.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [p.name, p.scope_value, p.mode, p.business_id, p.notes]
      .filter(Boolean).some(v => v!.toLowerCase().includes(q));
  });

  const modeBadge = (mode?: string | null) => {
    if (!mode) return <span className="text-xs text-gray-400 italic">runtime default</span>;
    const cls = mode === 'SIPRefer'
      ? 'bg-purple-100 text-purple-800'
      : mode === 'SIPDial'
        ? 'bg-indigo-100 text-indigo-800'
        : 'bg-green-100 text-green-800';
    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{mode}</span>;
  };

  if (loading && profiles.length === 0) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Transfer Profiles</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Per-destination AI transfer config (mode + Refer-To/Dial templates + headers). Synced to the voiceflow runtime.
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={isPlatformAdmin && !orgId}
          title={isPlatformAdmin && !orgId ? 'Select an organization first' : 'Add transfer profile'}
          className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
          Add Profile
        </button>
      </div>

      {isPlatformAdmin && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId}
            onChange={(value) => setSelectedOrgId(value || '')}
            className="w-full md:w-96"
            allowServiceProviderSelection={true}
          />
          {!orgId && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Select an organization to view and manage its transfer profiles.
            </p>
          )}
        </div>
      )}

      {/* Resolved provider + transfer capabilities for this org */}
      {caps && (
        caps.resolved ? (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-gray-700 dark:text-gray-200">
              Routes via <b>{caps.providerName}</b> <span className="text-gray-500">({caps.engineType}{caps.routingMode ? ` · ${caps.routingMode}` : ''})</span>
            </span>
            <span className={caps.supportsRefer ? 'text-green-600 dark:text-green-400' : 'text-gray-400 line-through'}>SIPRefer {caps.supportsRefer ? '✓' : '✗'}</span>
            <span className={caps.supportsDial ? 'text-green-600 dark:text-green-400' : 'text-gray-400 line-through'}>SIPDial {caps.supportsDial ? '✓' : '✗'}</span>
            <span className={caps.supportsConference ? 'text-green-600 dark:text-green-400' : 'text-gray-400 line-through'}>Conference {caps.supportsConference ? '✓' : '✗'}</span>
            <span className={(caps.supportsDial || caps.supportsConference) ? 'text-green-600 dark:text-green-400' : 'text-gray-400 line-through'}>liveAgent {(caps.supportsDial || caps.supportsConference) ? '✓' : '✗'}</span>
          </div>
        ) : (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200">
            No usable provider is resolved for this org yet — assign a provider (Providers → Business Routing) so transfer modes can be capability-checked.
          </div>
        )
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, destination, mode…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {error && <ErrorMessage message={error} onRetry={load} />}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                {['Name', 'Destination', 'Mode', 'Template', 'Headers', 'Status', 'Actions'].map(h => (
                  <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-center' : 'text-left'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  No transfer profiles{orgId ? ' for this organization' : ''} yet.
                </td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <PhoneForwarded className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="font-medium text-gray-900 dark:text-white">{p.name || '—'}</span>
                    </div>
                    {p.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-xs">{p.notes}</p>}
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900 dark:text-white font-mono">{p.scope_value}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{p.scope_type}</p>
                  </td>
                  <td className="px-6 py-4">{modeBadge(p.mode)}</td>
                  <td className="px-6 py-4">
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-mono truncate max-w-xs"
                       title={p.refer_to_template || p.dial_dest_template || ''}>
                      {p.refer_to_template || p.dial_dest_template || '—'}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {p.headers ? Object.keys(p.headers).length : 0}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${p.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                      {p.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(p)} disabled={deleting === p.id}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50" title="Delete">
                        {deleting === p.id
                          ? <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                          : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editing ? 'Edit Transfer Profile' : 'Add Transfer Profile'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{formError}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input type="text" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder="e.g. Cassava billing" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scope type</label>
                  <select value={form.scope_type} onChange={(e) => setForm({ ...form, scope_type: e.target.value as TransferProfile['scope_type'] })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                    {SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Destination {form.scope_type === 'default' ? '(any)' : form.scope_type === 'destination_series' ? '(prefix)' : '(number)'}
                  </label>
                  <input type="text" value={form.scope_type === 'default' ? '*' : (form.scope_value || '')}
                         disabled={form.scope_type === 'default'}
                         onChange={(e) => setForm({ ...form, scope_value: e.target.value })}
                         placeholder="e.g. 263772000111" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60 font-mono" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mode</label>
                <select value={form.mode || ''} onChange={(e) => setForm({ ...form, mode: (e.target.value || null) as TransferProfile['mode'] })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="">(no override — use runtime default)</option>
                  {MODES.map(m => (
                    <option key={m} value={m}>{m}{modeSupported(m) ? '' : ' — provider can’t execute'}</option>
                  ))}
                </select>
                {form.mode && !modeSupported(form.mode) && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    The resolved provider{caps?.providerName ? ` (${caps.providerName})` : ''} can’t execute <b>{form.mode}</b> — saving will be rejected.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Refer-To template <span className="text-gray-400">(SIPRefer)</span></label>
                <input type="text" value={form.refer_to_template || ''} onChange={(e) => setForm({ ...form, refer_to_template: e.target.value })}
                       placeholder="sip:{newNumber}@trunk" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dial destination template <span className="text-gray-400">(SIPDial)</span></label>
                <input type="text" value={form.dial_dest_template || ''} onChange={(e) => setForm({ ...form, dial_dest_template: e.target.value })}
                       placeholder="PJSIP/{newNumber}@tata-tele-cloud-sip-endpoint" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Headers <span className="text-gray-400">(value templates like {'{apartyNo}'} allowed)</span>
                </label>
                <div className="space-y-2">
                  {headerRows.length === 0 && (
                    <p className="text-xs text-gray-400 italic">No custom headers.</p>
                  )}
                  {headerRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text" value={row.key}
                        onChange={(e) => setHeaderRows(rows => rows.map((r, j) => j === i ? { ...r, key: e.target.value } : r))}
                        placeholder="Header name (e.g. X-Caller)"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                      />
                      <input
                        type="text" value={row.value}
                        onChange={(e) => setHeaderRows(rows => rows.map((r, j) => j === i ? { ...r, value: e.target.value } : r))}
                        placeholder="Value (e.g. {apartyNo})"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                      />
                      <button onClick={() => setHeaderRows(rows => rows.filter((_, j) => j !== i))}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg" title="Remove header">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => setHeaderRows(rows => [...rows, { key: '', value: '' }])}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                    <Plus className="w-4 h-4" /> Add header
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <input type="text" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                       className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                       className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Enabled</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                      className="tl-btn tl-btn-primary disabled:opacity-50">
                {saving && <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                {editing ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferProfiles;
